import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Divider, Chip,
  useTheme, Alert, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoIcon from '@mui/icons-material/Info';
import MapView from './MapView';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import {
  readFileAsText, parseAnyGeoFile, calculateCumulativeDistances,
} from '../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';

const COLORS = ['#6366f1', '#f43f5e', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

function Graphify() {
  const theme = useTheme();
  const [geoData, setGeoData] = useState(null);
  const [error, setError] = useState('');
  const [xAxis, setXAxis] = useState('distance'); // 'time', 'distance', 'index'
  const [selectedSeries, setSelectedSeries] = useState([]);
  const [hoverIndex, setHoverIndex] = useState(null);
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(600);

  // Resize observer for chart
  useEffect(() => {
    if (containerRef.current) {
      const observer = new ResizeObserver(() => {
        if (containerRef.current) setChartWidth(containerRef.current.clientWidth - 40);
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }
  }, [geoData]);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (result.error || !result.geoJson) throw new Error(result.error || 'Could not parse file');

      // Flatten features into a stream of points with properties
      const points = [];
      result.geoJson.features.forEach(f => {
        if (f.geometry?.type === 'Point') {
          points.push({
            coords: f.geometry.coordinates,
            props: f.properties || {},
          });
        } else if (f.geometry?.type === 'LineString') {
          f.geometry.coordinates.forEach((c, i) => {
            points.push({
              coords: c,
              props: f.properties?.coordinateProperties?.[i] || f.properties || {},
            });
          });
        }
      });

      if (points.length === 0) throw new Error('No track points found in data');

      // Calculate cumulative distances
      const coords = points.map(p => p.coords);
      const distances = calculateCumulativeDistances(coords);
      points.forEach((p, i) => p.distance = distances[i]);

      setGeoData(points);
      setError('');

      // Auto-select numeric properties
      const firstProps = points[0].props;
      const numericKeys = Object.keys(firstProps).filter(k => typeof firstProps[k] === 'number' || !isNaN(parseFloat(firstProps[k])));
      setSelectedSeries(numericKeys.slice(0, 3));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const chartData = useMemo(() => {
    if (!geoData) return null;

    const xValues = geoData.map((p, i) => {
      if (xAxis === 'time') {
        const ts = p.props.timestamp || p.props.time || p.props.t;
        return ts ? new Date(ts).getTime() : i;
      }
      if (xAxis === 'distance') return p.distance;
      return i;
    });

    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);

    const series = selectedSeries.map(key => {
      const vals = geoData.map(p => parseFloat(p.props[key]) || 0);
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      return { key, vals, min, max };
    });

    return { xValues, minX, maxX, series };
  }, [geoData, xAxis, selectedSeries]);

  const mapData = useMemo(() => {
    if (!geoData) return null;
    const features = [];
    const coords = geoData.map(p => p.coords);
    if (coords.length > 1) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { layer: 'route' },
      });
    }

    if (hoverIndex !== null && geoData[hoverIndex]) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: geoData[hoverIndex].coords },
        properties: { layer: 'hover', ...geoData[hoverIndex].props },
      });
    }

    return { type: 'FeatureCollection', features };
  }, [geoData, hoverIndex]);

  const styleFunction = useCallback((feature) => {
    if (feature.properties.layer === 'route') return { color: theme.palette.primary.main, weight: 3, opacity: 0.6 };
    return {};
  }, [theme]);

  const pointToLayerFn = useCallback((feature, latlng) => {
    if (feature.properties.layer === 'hover') {
      return L.circleMarker(latlng, {
        radius: 8,
        fillColor: theme.palette.secondary.main,
        color: '#fff',
        weight: 2,
        fillOpacity: 1,
      });
    }
    return L.circleMarker(latlng, { radius: 0 });
  }, [theme]);

  const renderChart = () => {
    if (!chartData) return null;
    const { xValues, minX, maxX, series } = chartData;
    const h = 250;
    const w = chartWidth;
    const pad = { top: 20, right: 20, bottom: 40, left: 50 };
    const innerW = w - pad.left - pad.right;
    const innerH = h - pad.top - pad.bottom;

    const getX = (val) => pad.left + ((val - minX) / (maxX - minX || 1)) * innerW;

    return (
      <Box sx={{ position: 'relative', width: w, height: h, mt: 2, userSelect: 'none' }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          if (x >= pad.left && x <= w - pad.right) {
            const ratio = (x - pad.left) / innerW;
            const idx = Math.min(geoData.length - 1, Math.max(0, Math.round(ratio * (geoData.length - 1))));
            setHoverIndex(idx);
          }
        }}
        onMouseLeave={() => setHoverIndex(null)}
      >
        <svg width={w} height={h}>
          {/* Axes */}
          <line x1={pad.left} y1={h - pad.bottom} x2={w - pad.right} y2={h - pad.bottom} stroke={theme.palette.divider} />
          <line x1={pad.left} y1={pad.top} x2={pad.left} y2={h - pad.bottom} stroke={theme.palette.divider} />

          {/* X-Axis labels */}
          <text x={pad.left} y={h - 10} fontSize="10" fill={theme.palette.text.secondary}>{xAxis === 'time' ? 'Time' : xAxis === 'distance' ? 'Distance (m)' : 'Index'}</text>
          <text x={w - pad.right} y={h - 10} fontSize="10" fill={theme.palette.text.secondary} textAnchor="end">{maxX.toFixed(0)}</text>

          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map(r => (
            <line key={r} x1={pad.left} y1={pad.top + r * innerH} x2={w - pad.right} y2={pad.top + r * innerH} stroke={theme.palette.divider} strokeDasharray="4 4" />
          ))}

          {/* Series */}
          {series.map((s, si) => {
            const points = s.vals.map((v, i) => `${getX(xValues[i])},${pad.top + innerH - ((v - s.min) / (s.max - s.min || 1)) * innerH}`).join(' ');
            return (
              <polyline key={s.key} points={points} fill="none" stroke={COLORS[si % COLORS.length]} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" style={{ transition: 'all 0.3s' }} />
            );
          })}

          {/* Hover indicator */}
          {hoverIndex !== null && (
            <line x1={getX(xValues[hoverIndex])} y1={pad.top} x2={getX(xValues[hoverIndex])} y2={h - pad.bottom} stroke={theme.palette.secondary.main} strokeWidth="1.5" strokeDasharray="2 2" />
          )}
        </svg>

        {/* Hover Tooltip */}
        <AnimatePresence>
          {hoverIndex !== null && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              style={{
                position: 'absolute',
                top: 0,
                left: getX(xValues[hoverIndex]) + 10,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px',
                padding: '8px',
                boxShadow: theme.shadows[4],
                pointerEvents: 'none',
                zIndex: 10,
                minWidth: '150px',
              }}
            >
              <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5, color: theme.palette.text.secondary }}>
                {xAxis === 'distance' ? `${xValues[hoverIndex].toFixed(0)}m` : xAxis === 'time' ? new Date(xValues[hoverIndex]).toLocaleTimeString() : `Point #${hoverIndex}`}
              </Typography>
              {series.map((s, si) => (
                <Stack key={s.key} direction="row" justifyContent="space-between" spacing={2}>
                  <Typography variant="caption" sx={{ color: COLORS[si % COLORS.length], fontWeight: 600 }}>{s.key}:</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{s.vals[hoverIndex].toFixed(2)}</Typography>
                </Stack>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    );
  };

  const numericKeys = useMemo(() => {
    if (!geoData || geoData.length === 0) return [];
    const firstProps = geoData[0].props;
    return Object.keys(firstProps).filter(k => typeof firstProps[k] === 'number' || !isNaN(parseFloat(firstProps[k])));
  }, [geoData]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="Graphify | Telemetry Data Visualizer | UmbrellaCo"
        description="Graphify your geospatial data. Plot speed, altitude, and other telemetry signals against time or distance with interactive map synchronization."
        keywords="graphify, telemetry visualizer, gps speed graph, altitude profile, gpx grapher, geojson telemetry"
        url="/spatial-tools/graphify"
        jsonLd={createSoftwareAppSchema(
          'Graphify',
          'Visualize telemetry data from GeoJSON, GPX, or CSV files with interactive charts.',
          'https://umbrellaco.in/spatial-tools/graphify',
          ['Telemetry Visualization', 'GPS Data Analysis', 'Interactive Charts']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>Graphify</Typography>
        <Typography variant="body2" color="text.secondary">
          Visualize telemetry signals (speed, elevation, battery, etc.) as interactive graphs synced with a map.
        </Typography>
      </Box>

      <PanelGroup direction="horizontal">
        <Panel defaultSize={30} minSize={20}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ p: 2 }}>
              <Button component="label" variant="contained" fullWidth size="small" startIcon={<UploadFileIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)' }
                }}>
                {geoData ? `Loaded (${geoData.length} pts)` : 'Upload Data'}
                <input type="file" hidden accept=".geojson,.json,.csv,.gpx,.kml" onChange={handleFileUpload} />
              </Button>
              {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            </Box>

            <Divider />

            {geoData && (
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Chart Configuration</Typography>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>X-Axis Scale</InputLabel>
                  <Select value={xAxis} label="X-Axis Scale" onChange={(e) => setXAxis(e.target.value)}>
                    <MenuItem value="distance">Distance (Meters)</MenuItem>
                    <MenuItem value="time">Time (Seconds)</MenuItem>
                    <MenuItem value="index">Index (Point Count)</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block' }}>Y-Axis Series</Typography>
                <Stack spacing={0.5}>
                  {numericKeys.map((key) => {
                    const seriesIndex = selectedSeries.indexOf(key);
                    const isSelected = seriesIndex !== -1;
                    return (
                      <Box key={key} sx={{
                        display: 'flex', alignItems: 'center', p: 0.5, borderRadius: 1,
                        bgcolor: isSelected ? `${COLORS[seriesIndex % COLORS.length]}11` : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedSeries([...selectedSeries, key]);
                            else setSelectedSeries(selectedSeries.filter(k => k !== key));
                          }}
                          sx={{
                            color: isSelected ? COLORS[seriesIndex % COLORS.length] : 'inherit',
                            '&.Mui-checked': { color: isSelected ? COLORS[seriesIndex % COLORS.length] : 'inherit' }
                          }}
                        />
                        <Typography variant="body2" sx={{
                          flexGrow: 1,
                          color: isSelected ? COLORS[seriesIndex % COLORS.length] : 'text.primary',
                          fontWeight: isSelected ? 700 : 400
                        }}>{key}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Divider />

            {geoData && (
              <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto' }}>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                  <InfoIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Data Inspection</Typography>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Prop</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem' }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {hoverIndex !== null && Object.entries(geoData[hoverIndex].props).map(([k, v]) => (
                        <TableRow key={k}>
                          <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{k}</TableCell>
                          <TableCell sx={{ fontSize: '0.7rem', py: 0.5, fontFamily: 'monospace' }}>{String(v)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </Panel>

        <PanelResizeHandle style={{ width: '5px', background: theme.palette.divider, cursor: 'col-resize' }} />

        <Panel defaultSize={70} minSize={40}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Map Area */}
            <Box sx={{ flex: 1, position: 'relative' }}>
              <MapView mapId="graphify-map" geoJsonData={mapData} styleFunction={styleFunction} pointToLayerFn={pointToLayerFn} />
            </Box>

            <PanelResizeHandle style={{ height: '5px', background: theme.palette.divider, cursor: 'row-resize' }} />

            {/* Chart Area */}
            <Box ref={containerRef} sx={{ height: 320, p: 2, bgcolor: 'background.paper', borderTop: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1}>
                  <TimelineIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="subtitle1" fontWeight={700}>Telemetry Plot</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {selectedSeries.map((s, i) => (
                    <Chip key={s} size="small" label={s} sx={{
                      bgcolor: `${COLORS[i % COLORS.length]}22`,
                      color: COLORS[i % COLORS.length],
                      fontWeight: 700,
                      border: `1px solid ${COLORS[i % COLORS.length]}44`
                    }} />
                  ))}
                </Stack>
              </Stack>
              {chartData ? renderChart() : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.disabled">Upload data to see the graph</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}

// Leaflet fallback
const L = window.L || {};

export default Graphify;
